import { StrictMode } from "react"
import { createRoot } from "react-dom/client"
import { RouterProvider, createRouter } from "@tanstack/react-router"

import "./styles/globals.css"

import { queryClient } from "@/lib/query-client"
import { RouteError, RouteNotFound, RoutePending } from "@/lib/router/fallbacks"
import { routeTree } from "./routeTree.gen"

/**
 * App entry. TanStack Router is created here (outside React) so its
 * route tree is reused across HMR cycles, devtools can attach, and the
 * typed context is available to `beforeLoad`/`loader` at module-init
 * time.
 *
 * `RouterContext` is the shape every route's `beforeLoad`/`loader`
 * receives. Right now it's just the shared QueryClient — add to it
 * (e.g. an auth helper) as the app grows; it's type-checked at every
 * route callsite.
 */
export type RouterContext = {
  queryClient: typeof queryClient
}

const router = createRouter({
  routeTree,
  context: {
    queryClient,
  } satisfies RouterContext,
  // Prefetch route code + loader data on hover/focus.
  defaultPreload: "intent",
  // Re-run the loader on real navigation; React Query's own staleTime
  // decides whether the backend actually gets hit (no double-fetch).
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
  defaultPendingComponent: RoutePending,
  defaultErrorComponent: RouteError,
  defaultNotFoundComponent: RouteNotFound,
})

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

const rootEl = document.getElementById("root")!
createRoot(rootEl).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>
)
